import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, DialogActions, DialogContent, Stack } from '@mui/material';
import { StyledDialog } from 'common/components/StyledDialog/StyledDialog';
import { StyledDialogTitle } from 'common/components/StyledDialog/StyledDialogTitle';
import { Checkbox, FormCheckBoxGroup } from 'common/form/components/FormCheckboxGroup/FormCheckboxGroup';
import { DialogComponentProps } from 'common/hooks/useDialog';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import {
  SortDirection,
  useAdminGetStaffQuery,
  useAdminUpdateStaffMutation,
  useCurrentUserQuery,
  useServiceLocationsQuery,
} from 'generated/graphql';
import { useContext, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { StyledDialogFormError } from '../../../common/components/StyledDialog/StyledDialogFormError';
import { styles } from './ChangeServiceLocations.styles';

export interface FormValues {
  serviceLocationChecks: (boolean | undefined)[];
}

export type ChangeServiceLocationsDialogProps = DialogComponentProps<void>;

export const ChangeServiceLocationsDialog: React.FC<ChangeServiceLocationsDialogProps> = ({
  isOpen,
  onReject,
  onResolve,
}) => {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading: loadingAuth } = useContext(AuthContext);
  const { data: currentUserData, loading: loadingCurrentUser } = useCurrentUserQuery();
  const { data: staffData, loading: loadingStaff } = useAdminGetStaffQuery({
    skip: !isAuthenticated,
    variables: { id: currentUserData?.user?.staffId ?? '' },
  });

  const staff = staffData?.adminGetStaff;

  const [adminUpdateStaff, { error, loading }] = useAdminUpdateStaffMutation({});
  const { data: serviceLocationsData, loading: loadingServiceLocations } = useServiceLocationsQuery({
    variables: { sortInput: { by: [{ direction: SortDirection.Asc, fieldPath: 'name' }] } },
  });

  const serviceLocationIds = staff?.serviceLocations.map((serviceLocation) => serviceLocation.id);
  const serviceLocations = serviceLocationsData?.serviceLocations?.items;

  const schema: yup.SchemaOf<FormValues> = yup.object({
    serviceLocationChecks: yup.array(),
  });

  const formMethods = useForm<FormValues>({
    defaultValues: {
      serviceLocationChecks: [],
    },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    formMethods.setValue(
      'serviceLocationChecks',
      serviceLocations?.map((serviceLocation) => serviceLocationIds?.includes(serviceLocation.id)) ?? [],
    );
  }, [serviceLocations, serviceLocationIds, formMethods]);

  if (loadingAuth || !isAuthenticated || loadingCurrentUser || loadingStaff || !staff || loadingServiceLocations)
    return null;

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    if (data.serviceLocationChecks) {
      const resp = await adminUpdateStaff({
        variables: {
          id: staff.id,
          input: {
            serviceAreas: staff.serviceAreas,
            serviceLocationIds:
              (data.serviceLocationChecks
                ?.map((checked, index) => (checked ? serviceLocations?.[index].id : null))
                .filter((val) => val != null) as string[]) ?? [],
          },
        },
      });

      if (resp) {
        onResolve();
      }
    }
  });

  const options: Checkbox[] =
    serviceLocations?.map((serviceLocation, index) => ({
      label: serviceLocation.name,
      name: index.toString(),
    })) ?? [];

  return (
    <FormProvider {...formMethods}>
      <StyledDialog PaperProps={{ sx: styles.dialog }} maxWidth="lg" onClose={() => onReject()} open={isOpen}>
        <Stack sx={styles.content}>
          <StyledDialogTitle textAlign="left">{t('settings.changeDialog.serviceLocations.title')}</StyledDialogTitle>
          <Box component="form" noValidate onSubmit={handleSubmit}>
            <DialogContent>
              <FormCheckBoxGroup
                columns={3}
                label={t('settings.changeDialog.serviceLocations.label')}
                name="serviceLocationChecks"
                options={options}
              />
            </DialogContent>
            <DialogActions>
              <Button color="secondaryButton" onClick={() => onReject()}>
                {t('settings.changeDialog.cancel')}
              </Button>
              <LoadingButton loading={loading} type="submit">
                {t('settings.changeDialog.save')}
              </LoadingButton>
            </DialogActions>
            {!!error && !loading && (
              <StyledDialogFormError content={error.message} title={t('settings.changeDialog.error')} />
            )}
          </Box>
        </Stack>
      </StyledDialog>
    </FormProvider>
  );
};
